import React, { useState } from 'react';
import {
    HeadingText50,
    HeadingText30,
    BodyTextMedium,
    Button,
    SectionHeaderSkeleton,
    PageHeaderSkeleton,
    ButtonGroup,
} from '@modernhealth/vital-components';
import { FormattedDate, FormattedTime } from 'react-intl';
import { useGetCalendarLinksQuery, useGetCirclesRsvpQuery } from '@modernhealth/circles-graphql/v2';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { ExternalLinkButton } from 'src/shared/components/ExternalLinkButton';
import { getUserInformation } from 'src/modules/user/selectors/userSelectors';
import { isAuthenticated } from 'src/modules/auth/selectors/authenticationSelectors';
import { useLastRouteInHistory } from 'src/routing/RouteContext';

import { ReactComponent as Confirmation } from '../assets/confirmation.svg';
import {
    CirclesAddToCalendarModal,
    useCalendarLinksForCircle,
} from '../components/CirclesAddToCalendarButton';
import { BackButton } from '../components/BackButton';
import { Page } from '../components/PageLayout';
import { TranslatedDuration } from '../components/TranslatedDuration';
import { CirclesErrorFallback } from '../components/CirclesErrorFallback';
import { InlineBulletList } from '../components/InlineBulletList';

interface CirclesRsvpConfirmationTemplateProps {
    secondaryAction: React.ReactNode;
    rsvpId: string;
}

export const CirclesRsvpConfirmationTemplate: React.FC<CirclesRsvpConfirmationTemplateProps> = ({
    secondaryAction,
    rsvpId,
}) => {
    const userIsAuthenticated = useSelector(isAuthenticated);
    const { email } = useSelector(getUserInformation, (a, b) => a.email === b.email);

    const calendarLinksQuery = useGetCalendarLinksQuery({
        variables: {
            rsvpId,
        },
    });
    const rsvpQuery = useGetCirclesRsvpQuery({
        variables: {
            id: rsvpId,
        },
    });

    const { t } = useTranslation('circles');

    const [showModal, setShowModal] = useState(false);

    const calendarLinks = useCalendarLinksForCircle(calendarLinksQuery.data);

    if (rsvpQuery.loading || calendarLinksQuery.loading) {
        return <Skeleton />;
    }

    if (rsvpQuery.data?.circleRsvp?.__typename !== 'CircleRsvpNode') {
        return (
            <CirclesErrorFallback
                onRetry={() => {
                    rsvpQuery.refetch();
                }}
            />
        );
    }

    const rsvp = rsvpQuery.data.circleRsvp;
    const session = rsvp.session;
    const topic = session.topic;

    return (
        <PageLayout>
            <CirclesAddToCalendarModal
                visible={showModal}
                onClose={() => setShowModal(false)}
                calendarLinks={calendarLinks}
            />
            <Confirmation height="15rem" width="15rem" css={{ marginBottom: '3rem' }} />
            <HeadingText50 as="h2">{t('pages.rsvp_confirmation.title')}</HeadingText50>

            <HeadingText30 css={{ marginBottom: '1rem' }}>{topic.name}</HeadingText30>

            {session?.startTime ? (
                <BodyTextMedium fontColor="gray55" css={{ marginBottom: '1rem' }}>
                    <InlineBulletList>
                        <FormattedDate
                            weekday="short"
                            month="short"
                            day="numeric"
                            value={session.startTime}
                        />
                        <FormattedTime
                            hour="numeric"
                            minute="numeric"
                            timeZoneName="short"
                            value={session.startTime}
                        />
                        <span>
                            <TranslatedDuration minutes={topic.duration} />
                        </span>
                    </InlineBulletList>
                </BodyTextMedium>
            ) : null}
            <BodyTextMedium fontColor="gray55" numberOfLines={3}>
                {t(
                    'pages.rsvp_confirmation.instructions',
                    email && userIsAuthenticated ? { context: 'email', email } : {},
                )}
            </BodyTextMedium>
            <BodyTextMedium css={{ marginBottom: '32px' }} fontColor="gray55">
                {t('pages.rsvp_confirmation.note')}
            </BodyTextMedium>
            <ButtonGroup>
                {secondaryAction}

                <RsvpActionButton
                    meetingUrl={rsvp.joinSessionUrl}
                    isJoinable={session.isJoinable}
                    loading={calendarLinksQuery.loading || rsvpQuery.loading}
                    onCalendarClick={() => {
                        setShowModal(true);
                    }}
                />
            </ButtonGroup>
        </PageLayout>
    );
};

const RsvpActionButton = ({
    meetingUrl,
    isJoinable,
    onCalendarClick,
    loading,
}: {
    meetingUrl?: string | null;
    isJoinable: boolean;
    onCalendarClick: () => void;
    loading: boolean;
}) => {
    const { t } = useTranslation('circles');

    return meetingUrl && isJoinable ? (
        <ExternalLinkButton variant="primary" size="large" href={meetingUrl}>
            {t('session.rsvp.join_button.title')}
        </ExternalLinkButton>
    ) : (
        <Button variant="primary" size="large" disabled={loading} onClick={onCalendarClick}>
            {t('pages.rsvp_confirmation.actions.add_to_calendar')}
        </Button>
    );
};

const Skeleton = (): JSX.Element => {
    return (
        <PageLayout>
            <PageHeaderSkeleton />
            <SectionHeaderSkeleton />
        </PageLayout>
    );
};

const PageLayout: React.FC = ({ children }) => {
    const hasLastRoute = !!useLastRouteInHistory();

    return (
        <Page.Container>
            <Page.Header>{hasLastRoute ? <BackButton /> : null}</Page.Header>
            <Page.Content
                centered
                alignChildren="center"
                css={{
                    paddingTop: 70,
                    maxWidth: 700,
                }}
            >
                {children}
            </Page.Content>
        </Page.Container>
    );
};
